// Contextual AI assist for the capture command room. The proposal-person
// complaint was "I don't know when or why to use the chat" — so instead of a
// bare chat entry, these are point-of-need actions with concrete prompts
// ("Summarize interactions", "Draft a black-hat analysis"). Each mints a chat
// scoped to the capture's backing zou project (so the agent has the pursuit's
// context) and seeds the prompt via the same sessionStorage handoff the
// assistant page uses, so the answer starts immediately.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Sparkles } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateProjectChat } from '../../hooks/useProjectChats';
export function useCaptureAi(zouProjectId) {
    const navigate = useNavigate();
    const createChat = useCreateProjectChat();
    const ask = useCallback(async (prompt)=>{
        if (!zouProjectId || createChat.isPending) return;
        try {
            const resp = await createChat.mutateAsync({
                projectId: zouProjectId,
                data: {}
            });
            const chatId = resp.chat.chatId;
            try {
                // ChatView auto-sends this on mount (the "navigating from assistant
                // page" path) — see ChatView's init:: handler.
                sessionStorage.setItem(`init::${chatId}`, JSON.stringify({
                    prompt
                }));
            } catch  {
            // Storage unavailable — the chat still opens; the user can type.
            }
            navigate(`/project/${zouProjectId}/chat/${chatId}`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to start AI chat');
        }
    }, [
        zouProjectId,
        createChat,
        navigate
    ]);
    return {
        ask,
        isPending: createChat.isPending
    };
}
export function AiAssistButton({ zouProjectId, prompt, label, action, variant = 'outline' }) {
    const { ask, isPending } = useCaptureAi(zouProjectId);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: variant === 'ghost' ? 'ghost' : 'secondary',
        size: "sm",
        disabled: isPending,
        "data-testid": Testids.capture.aiAssistAction,
        "data-action": action,
        onClick: ()=>ask(prompt),
        iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
            className: "h-3.5 w-3.5"
        }),
        children: label
    });
}
// Capture-wide assist bar for the Overview tab: an explainer + the highest-value
// starter actions, so the value proposition is unmissable.
export function AiAssistBar({ zouProjectId }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border bg-muted/20 p-3",
        "data-testid": Testids.capture.aiAssist,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1.5 text-sm font-medium",
                children: [
                    /*#__PURE__*/ _jsx(Sparkles, {
                        className: "h-4 w-4 text-primary"
                    }),
                    " AI assist"
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mb-2 mt-1 text-xs text-muted-foreground",
                children: "The assistant already has this pursuit's context — ask it to assess the opportunity, summarize what you've logged, draft analyses, or suggest your next moves."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(AiAssistButton, {
                        zouProjectId: zouProjectId,
                        action: "assess",
                        label: "Assess this pursuit",
                        prompt: "Assess this capture pursuit: what's our likely win theme, what are the biggest gaps or risks, and what are the top three things we should do next?"
                    }),
                    /*#__PURE__*/ _jsx(AiAssistButton, {
                        zouProjectId: zouProjectId,
                        action: "summarize-interactions",
                        label: "Summarize interactions",
                        prompt: "Summarize the customer interactions logged on this capture. Call out positive signals, risks, and the follow-ups we owe."
                    }),
                    /*#__PURE__*/ _jsx(AiAssistButton, {
                        zouProjectId: zouProjectId,
                        action: "black-hat",
                        label: "Draft black-hat analysis",
                        prompt: "Draft a black-hat competitive analysis for this pursuit: who are the likely competitors and the incumbent, what are their strengths and vulnerabilities, and how do we position against them?"
                    })
                ]
            })
        ]
    });
}
